import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, MoreVertical, Edit, Handshake, UserCheck, Calendar, Camera, MessageSquareShare, MessageCircle } from 'lucide-react';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';
import { ClientAvatar } from '../clients/ClientAvatar';
import { supabase } from '../../lib/supabase';
import { useClientStore } from '../../lib/stores/clientStore';
import toast from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { ShareToChatModal } from '../team-chat/ShareToChatModal';
import { useOpenWhatsAppChat } from '../whatsapp-overlay/useOpenWhatsAppChat';

interface LeadDetailsHeaderProps {
  selectedClient: any;
  // Task 2 "Partner drawer unificato": tipo esteso dell'entità mostrata (include
  // 'partner'). Task 4: branch di rendering effettivo (back target, avatar,
  // menu azioni, permessi) per entityType === 'partner'.
  entityType?: 'lead' | 'client' | 'partner';
  isLead: boolean;
  showActionsMenu: boolean;
  setShowActionsMenu: (show: boolean) => void;
  isConverting: boolean;
  onEditLead: () => void;
  // Task 4 "Partner drawer unificato": callback dedicata alla modifica partner
  // (collegata a PartnerForm dal Task 5). Usata al posto di onEditLead quando
  // entityType === 'partner'; opzionale, per ora può restare undefined.
  onEdit?: () => void;
  onCreateDeal: () => void;
  onConvertToClient: () => void;
  detailsT: any;
  isModal?: boolean;
  onClose?: () => void;
}

export function LeadDetailsHeader({
  selectedClient,
  entityType,
  isLead,
  showActionsMenu,
  setShowActionsMenu,
  isConverting,
  onEditLead,
  onEdit,
  onCreateDeal,
  onConvertToClient,
  detailsT,
  isModal = false,
  onClose
}: LeadDetailsHeaderProps) {
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { lead: leadLabel, client: clientLabel } = useMenuLabels();

  // Task 4 "Partner drawer unificato": branch entità partner (external_partners) —
  // niente upload logo, niente Crea Trattativa/Convert, permesso dedicato.
  const isPartner = entityType === 'partner';

  // Memoize permission checks to avoid recalculation on every render
  const canEdit = useMemo(() =>
    !permissionsLoading && hasPermission(isPartner ? 'external_partners' : (isLead ? 'leads' : 'clients'), 'update'),
    [hasPermission, isPartner, isLead, permissionsLoading]
  );

  const canCreateDeal = useMemo(() =>
    !permissionsLoading && hasPermission('deals', 'create'),
    [hasPermission, permissionsLoading]
  );

  const canConvert = useMemo(() =>
    !permissionsLoading && hasPermission('leads', 'convert'),
    [hasPermission, permissionsLoading]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateClient = useClientStore(state => state.updateClient);
  const [showShareToChat, setShowShareToChat] = useState(false);

  // Apre il drawer sulla chat del lead senza lasciare la scheda; con piu'
  // numeri aziendali chiede quale usare, ancorato al pulsante. Il pulsante
  // compare solo se l'utente vede almeno un numero (WAHA o Cloud, rispettando
  // gli accessi per utente), non piu' "se l'org ha una riga waha".
  const { canWhatsApp: hasWhatsApp, openChat: openWhatsAppChat, chooser: waChooser } = useOpenWhatsAppChat();
  const leadPhone = selectedClient?.whatsapp_number || selectedClient?.phone;
  const canWhatsApp = hasWhatsApp && leadPhone && leadPhone.trim().length > 0;

  // "Ultima attività" relativa sotto il nome: risponde ad "a che punto siamo?"
  // senza leggere la cronologia.
  const lastActivityLabel = useMemo(() => {
    if (!selectedClient?.last_activity_date) return null;
    const diffMs = Date.now() - new Date(selectedClient.last_activity_date).getTime();
    if (diffMs < 0) return null;
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return minutes <= 1 ? 'adesso' : `${minutes} min fa`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? '1 ora fa' : `${hours} ore fa`;
    const days = Math.floor(hours / 24);
    if (days < 30) return days === 1 ? 'ieri' : `${days} giorni fa`;
    return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
      .format(new Date(selectedClient.last_activity_date));
  }, [selectedClient?.last_activity_date]);


  const handleOpenWhatsApp = (e?: React.MouseEvent<HTMLElement>) => {
    if (!leadPhone) return;
    openWhatsAppChat(leadPhone, e?.currentTarget.getBoundingClientRect() ?? null, null, selectedClient?.business_name);
  };

  // Voce del menu azioni: stesso linguaggio dei menu del kanban e del selettore
  // numero WhatsApp (12,5px, icona sottile grigia, sfondo appena accennato).
  const voceMenu =
    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-[#37352F] transition-colors ' +
    'hover:bg-[rgba(55,53,47,0.06)] disabled:opacity-50 dark:text-white dark:hover:bg-white/[0.06]';

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClient.id) return;

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('Immagine troppo grande (max 2MB)');
      return;
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp', 'svg'].includes(fileExt || '')) {
      toast.error('Formato non supportato. Usa JPG, PNG, WebP o SVG');
      return;
    }

    try {
      const orgId = selectedClient.organization_id;
      const fileName = `${orgId}/clients/${selectedClient.id}_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('company_logos')
        .upload(fileName, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company_logos')
        .getPublicUrl(fileName);

      await updateClient(selectedClient.id, { logo_url: publicUrl });
      toast.success('Logo aggiornato');
    } catch (err) {
      console.error('[LeadDetailsHeader] Logo upload error:', err);
      toast.error('Errore nel caricamento del logo');
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Debug logging when menu opens
  React.useEffect(() => {
    if (showActionsMenu && !permissionsLoading) {
      console.log('[LeadDetailsHeader] Menu aperto con permessi:', {
        isLead,
        canEdit,
        canCreateDeal,
        canConvert,
        entityType: isLead ? 'lead' : 'client'
      });
    }
  }, [showActionsMenu, permissionsLoading, canEdit, canCreateDeal, canConvert, isLead]);

  return (
    <div className={`${isModal ? 'mb-2' : 'mb-4'} relative flex-shrink-0`}>
      <div>
        <div className="px-2 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              {!isModal && (
                <button
                  onClick={() => navigate(isPartner ? '/partners' : (isLead ? '/leads' : '/clients'))}
                  className="text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white/80 transition-colors p-1"
                  type="button"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <div className="relative group">
                <ClientAvatar
                  client={selectedClient}
                  size="md"
                  onClick={!isPartner && canEdit ? () => fileInputRef.current?.click() : undefined}
                />
                {!isPartner && canEdit && (
                  <div
                    className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                )}
                {/* Task 4 "Partner drawer unificato": external_partners non ha logo_url,
                    nessun upload — input file inerte/non renderizzato per i partner. */}
                {!isPartner && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-semibold truncate text-gray-800 dark:text-white">
                  {selectedClient.business_name || (isPartner ? 'Partner' : (isLead ? leadLabel.label : clientLabel.label))}
                </h1>
                {/* Persona di riferimento (clients.contact_name, es. dai negozi: nome e cognome di chi ordina
                    per un'azienda). Non ripetuta quando coincide con il nome della scheda (persone). */}
                {(() => {
                  const referente = typeof selectedClient.contact_name === 'string' ? selectedClient.contact_name.trim() : '';
                  if (!referente || referente.toLowerCase() === String(selectedClient.business_name || '').trim().toLowerCase()) return null;
                  return (
                    <p className="text-sm text-[#37352F] dark:text-gray-200 truncate">
                      <span className="text-[#787774] dark:text-gray-400">Referente:</span> {referente}
                    </p>
                  );
                })()}
                {selectedClient.created_at && (
                  <div className="flex min-w-0 items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 mt-0.5">
                    <Calendar className="h-3 w-3 flex-shrink-0" />
                    <span className="min-w-0 truncate whitespace-nowrap">
                      {selectedClient.creator ? (
                        <>
                          Creato da <span className="text-[#37352F] dark:text-white font-medium">{selectedClient.creator.full_name || selectedClient.creator.email}</span> il {new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(selectedClient.created_at))}
                        </>
                      ) : (
                        <>Creato il {new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(selectedClient.created_at))}</>
                      )}
                      {lastActivityLabel && <> · Ultima attività {lastActivityLabel}</>}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* "Crea offerta" fuori dal kebab solo in pagina, dove c'è spazio
                  per l'etichetta. Nel modale (colonna stretta) niente icone
                  sfuse: tutte le azioni vivono nel kebab, con Crea offerta
                  prima voce evidenziata e WhatsApp subito sotto. */}
              {!isPartner && canCreateDeal && !isModal && (
                <button
                  onClick={onCreateDeal}
                  className="hidden sm:flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-white/90 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                  type="button"
                >
                  <Handshake className="h-4 w-4" />
                  Crea offerta
                </button>
              )}
              {canWhatsApp && !isModal && (
                <button
                  onClick={(e) => handleOpenWhatsApp(e)}
                  className="p-2 rounded-lg transition-colors text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                  type="button"
                  data-tooltip-id="lead-header-tip"
                  data-tooltip-content="Invia messaggio WhatsApp"
                >
                  <MessageCircle className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="p-2 rounded-lg transition-colors text-gray-600 dark:text-white/80 hover:text-gray-800 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10"
                type="button"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown menu rendered outside the blurred container */}
      {showActionsMenu && (
        <div className="absolute right-3 top-full z-[9998] mt-1 w-56 rounded-lg border border-[rgba(55,53,47,0.09)] bg-white py-1 shadow-lg dark:border-white/10 dark:bg-[#252528]">
          {permissionsLoading ? (
            <div className="px-3 py-1.5 text-[12.5px] text-[#787774] dark:text-gray-500">
              Carico i permessi…
            </div>
          ) : (
            <>
              {/* Task 4 "Partner drawer unificato": niente Crea Trattativa per un partner.
                  Prima voce evidenziata: è l'azione più frequente della scheda. */}
              {!isPartner && canCreateDeal && (
                <button
                  onClick={() => {
                    onCreateDeal();
                    setShowActionsMenu(false);
                  }}
                  className={voceMenu}
                >
                  <Handshake className="h-3.5 w-3.5 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                  Crea offerta
                </button>
              )}
              {/* Nel modale l'icona WhatsApp non è più nell'header: vive qui */}
              {canWhatsApp && isModal && (
                <button
                  onClick={(e) => {
                    handleOpenWhatsApp(e);
                    setShowActionsMenu(false);
                  }}
                  className={voceMenu}
                >
                  <MessageCircle className="h-3.5 w-3.5 flex-shrink-0 text-[#1FA855] dark:text-[#4ADE80]" strokeWidth={1.5} />
                  Invia WhatsApp
                </button>
              )}
              {((!isPartner && canCreateDeal) || (canWhatsApp && isModal)) && canEdit && (
                <div className="my-1 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10" />
              )}
              {canEdit && (
                <button
                  onClick={() => {
                    if (isPartner) {
                      onEdit?.();
                    } else {
                      onEditLead();
                    }
                    setShowActionsMenu(false);
                  }}
                  className={voceMenu}
                >
                  <Edit className="h-3.5 w-3.5 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                  {isPartner ? 'Modifica Partner' : (isLead ? `Modifica ${leadLabel.label}` : `Modifica ${clientLabel.label}`)}
                </button>
              )}
              {/* Task 4 "Partner drawer unificato": niente conversione per un partner. */}
              {!isPartner && isLead && canConvert && (
                <button
                  onClick={() => {
                    onConvertToClient();
                    setShowActionsMenu(false);
                  }}
                  className={voceMenu}
                  disabled={isConverting}
                >
                  <UserCheck className="h-3.5 w-3.5 flex-shrink-0 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                  {isConverting ? detailsT.details.converting : detailsT.conversion.button}
                </button>
              )}
              {!canEdit && (isPartner || !canCreateDeal) && (isPartner || !isLead || !canConvert) && (
                <div className="px-3 py-1.5 text-[12.5px] leading-snug text-[#787774] dark:text-gray-500">
                  Nessuna azione disponibile
                  <div className="text-[11px] text-[rgba(55,53,47,0.45)] dark:text-gray-600">Dipende dai tuoi permessi</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <Tooltip id="lead-header-tip" place="bottom" delayShow={300} className="!text-xs !py-1 !px-2 !rounded-md" />
      {/* Task 4 "Partner drawer unificato": condivisione in chat non applicabile a un partner. */}
      {waChooser}
      {showShareToChat && !isPartner && (
        <ShareToChatModal
          entityType="lead"
          entityId={selectedClient.id}
          entityData={{
            name: selectedClient.business_name,
            email: selectedClient.email || '',
            phone: selectedClient.phone || '',
            source: selectedClient.lead_source || '',
            stage: selectedClient.pipeline_stage?.name || selectedClient.client_state?.name || '',
          }}
          onClose={() => setShowShareToChat(false)}
        />
      )}
    </div>
  );
}